import { Button } from '@/components/ui/button';
import { CampoDeBusca } from '@/components/ui/campo-de-busca';
import type { SearchStrictness } from '@/lib/search/smartSearch';
import { cn } from '@/lib/utils';

export interface BuscaInteligenteProps {
  value: string;
  onChange: (value: string) => void;
  strictness: SearchStrictness;
  onStrictnessChange: (value: SearchStrictness) => void;
  /** Quantos sobraram e de quantos — só aparece quando há pergunta. */
  resultCount?: number;
  /** A contagem já escrita, quando quem filtra é o servidor ("12 intimações"). */
  contagem?: string;
  totalCount?: number;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  /** Lista sem número de processo: a ajuda não traz esse exemplo. */
  semNumero?: boolean;
}

const STRICTNESS_OPTIONS: { value: SearchStrictness; label: string; hint: string }[] = [
  { value: 'flexible', label: 'Flexível', hint: 'acha pedaço de palavra' },
  { value: 'strict', label: 'Rígida', hint: 'exige a palavra inteira' },
];

const SEARCH_FORMS: { example: string; explanation: string }[] = [
  { example: 'apelação fazenda', explanation: 'Acha quem tem as duas palavras, em qualquer ordem.' },
  { example: '"cumprimento de sentença"', explanation: 'Entre aspas, a frase exata.' },
  { example: 'agravo -regimental', explanation: 'O traço na frente exclui a palavra.' },
  { example: '0807420-34.2022', explanation: 'Número com ou sem pontuação, ou só o começo.' },
];

function summaryText(value: string, resultCount?: number, totalCount?: number): string {
  if (!value.trim() || resultCount === undefined || totalCount === undefined) return '';
  return `${resultCount} de ${totalCount}`;
}

/** O conteúdo do ⓘ: as formas de busca e, embaixo, o rigor (que não ocupa a barra). */
/**
 * O conteúdo do ⓘ da busca inteligente. Sem `onStrictnessChange` (a busca do painel,
 * que só leva a pergunta ao acervo), não mostra o rigor — ele se escolhe lá.
 */
export function AjudaDaBusca({
  strictness = 'flexible',
  onStrictnessChange,
  semNumero = false,
}: {
  strictness?: SearchStrictness;
  onStrictnessChange?: (value: SearchStrictness) => void;
  /** Lista sem número de processo (modelos, atendimentos): sem esse exemplo. */
  semNumero?: boolean;
}) {
  return (
    <>
      <p className="text-sm font-semibold">Como buscar</p>
      <p className="mt-1 text-xs text-muted-foreground">Acentos e maiúsculas não importam.</p>
      <dl className="mt-3 space-y-2">
        {(semNumero ? SEARCH_FORMS.filter((form) => !/^\d/.test(form.example)) : SEARCH_FORMS).map((form) => (
          <div key={form.example}>
            <dt>
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{form.example}</code>
            </dt>
            <dd className="mt-0.5 text-xs text-muted-foreground">{form.explanation}</dd>
          </div>
        ))}
      </dl>
      {onStrictnessChange && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs font-medium">Rigor</p>
          {/* Controle segmentado: uma moldura e a opção ativa destacada. */}
          <div
            role="group"
            aria-label="Rigor da busca"
            className="mt-1.5 inline-flex h-9 items-center gap-0.5 rounded-md border border-input bg-muted/40 p-0.5"
          >
            {STRICTNESS_OPTIONS.map((option) => {
              const selected = strictness === option.value;
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-pressed={selected}
                  onClick={() => onStrictnessChange(option.value)}
                  className={cn(
                    'h-full rounded-[5px] px-3 text-sm',
                    selected
                      ? 'bg-background font-medium text-foreground shadow-sm hover:bg-background'
                      : 'font-normal text-muted-foreground hover:bg-transparent hover:text-foreground',
                  )}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            <strong className="font-medium text-foreground">Flexível</strong> {STRICTNESS_OPTIONS[0].hint}:{' '}
            <em>apel</em> acha <em>apelação</em>. <strong className="font-medium text-foreground">Rígida</strong>{' '}
            {STRICTNESS_OPTIONS[1].hint}.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * A busca inteligente das telas de lista: o `CampoDeBusca` com a contagem "N de M"
 * embutida e, no ⓘ, as formas de busca e o rigor (Flexível/Rígida). A filtragem
 * em si fica no `useSmartSearch` da tela.
 */
export function BuscaInteligente({
  value,
  onChange,
  strictness,
  onStrictnessChange,
  resultCount,
  contagem,
  totalCount,
  placeholder = 'Buscar…',
  className,
  autoFocus,
  semNumero,
}: BuscaInteligenteProps) {
  const summary = summaryText(value, resultCount, totalCount);

  return (
    <div className={cn('min-w-0', className)}>
      <CampoDeBusca
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        contagem={contagem ?? (summary || undefined)}
        ajuda={<AjudaDaBusca strictness={strictness} onStrictnessChange={onStrictnessChange} semNumero={semNumero} />}
      />
    </div>
  );
}
